import axios from "axios";
import Family from "models/Family";
import Subfamily from "models/Subfamily";
import SavingsPlan from "models/SavingsPlan";
import StockVehicle from "models/StockVehicle";
import ServiceOffer from "models/ServiceOffer";
import { createContext, useContext, useState, useEffect } from "react";

const Context = createContext({ parameters: null, families: [] });

const API_URL = "https://sistema.yacopini.com.ar/intranet/webservice/v1/gm";

const ContextProvider = ({ children }) => {
  const [parameters, setParameters] = useState(null);
  const [families, setFamilies] = useState([]);
  const [offers, setOffers] = useState([]);
  const [serviceOffers, setServiceOffers] = useState([]);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    axios
      .get("/api/gm/parameters")
      .then((response) => setParameters(response.data))
      .catch((e) => console.error(e));

    axios
      .get(`${API_URL}/stock/families`)
      .then((response) =>
        setFamilies(response.data.data.map((data) => new Family(data)))
      )
      .catch((e) => console.error(e));

    axios
      .get(`${API_URL}/offers`)
      .then((response) =>
        setOffers(response.data.data.map((data) => new StockVehicle(data)))
      )
      .catch((e) => console.error(e));

    axios
      .get(`/api/gm/service-promotions`)
      .then((response) => {
        const serviceOffers = response.data.data.filter(
          ({ price_default, price_promotion }) =>
            price_default !== price_promotion
        );

        setServiceOffers(serviceOffers.map((data) => new ServiceOffer(data)));
      })
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    if (!families.length) {
      return;
    }

    axios
      .get(`${API_URL}/plan/find`)
      .then((response) => {
        const plans = response.data.data.map(
          (data) => new SavingsPlan(data, families)
        );
        plans.sort((a, b) => a.priority - b.priority);

        setPlans(plans);
      })
      .catch((e) => console.error(e));
  }, [families]);

  if (!parameters || !families.length) {
    return <div>Bienvenido a Yacopini Motors</div>;
  }

  return (
    <Context.Provider
      value={{ parameters, families, offers, serviceOffers, plans }}
    >
      {children}
    </Context.Provider>
  );
};

export const useParameter = (name) => {
  const { parameters } = useContext(Context);

  if (!parameters) {
    console.log("Parameters are not ready");

    return "";
  }

  if (!parameters[name]) {
    console.log(`Parameter ${name} does not exist`);

    return "";
  }

  return parameters[name];
};

export const useContactMethod = (department) => {
  const methods = useParameter("contact_methods");

  return methods.filter((method) => method.department === department);
};

export const useFamilies = () => {
  const { families } = useContext(Context);

  return families;
};

export const useFamiliesWithOffers = () => {
  const families = useFamilies();
  const offers = useOffers();

  families.forEach((family) => {
    family.offers = offers.filter((offer) => offer.family_id === family.id);
    family.best_offer = family.offers.reduce((best, offer) => {
      if (!best || offer.special_discount > best.special_discount) {
        return offer;
      }

      return best;
    }, null);
  });

  return families.filter((family) => family.offers.length > 0);
};

export const useFamily = (name) => {
  const { families } = useContext(Context);

  if (!name) {
    return null;
  }

  return families.find(
    (family) =>
      family.name.toLowerCase() === name.toLowerCase() ||
      family.url_name === name
  );
};

export const useSavingsPlan = (id) => {
  const { plans } = useContext(Context);

  if (!id) {
    console.log("Skipping empty id");
    return;
  }

  return plans.find((plan) => plan.id === id);
};

export const useSubfamilies = (family) => {
  const [subfamilies, setSubfamilies] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_URL}/stock/subfamilies?family_id=${family.id}`)
      .then((response) =>
        setSubfamilies(response.data.data.map((data) => new Subfamily(data)))
      )
      .catch((e) => console.error(e));
  }, [family]);

  return subfamilies;
};

export const useSubfamily = (family, name) => {
  const [subfamily, setSubfamily] = useState(null);
  const subfamilies = useSubfamilies(family);
  const subfamilyName = name ? name.replace("-", " ") : "";

  useEffect(() => {
    if (!subfamilies || !subfamilyName) {
      return;
    }

    subfamilies.forEach((subfamily) => {
      if (subfamily.name.toLowerCase() === subfamilyName) {
        setSubfamily(subfamily);
      }
    });
  }, [subfamilyName, subfamilies]);

  return subfamily;
};

export const useSavingPlans = () => {
  const { plans } = useContext(Context);

  return plans;
};

export const useOffers = (family) => {
  const { offers } = useContext(Context);

  if (!family) {
    return offers;
  }

  return offers.filter((offer) => offer.family_id === family.id);
};

export const useStock = (family, subfamily) => {
  const [stock, setStock] = useState([]);

  useEffect(() => {
    if (!subfamily) {
      return [];
    }

    getStock(family, subfamily).then((stock) => setStock(stock));
  }, [family, subfamily]);

  return stock;
};

export async function getStock(family, subfamily) {
  try {
    const response = await axios.get(
      `${API_URL}/stock/stock?family_id=${family.id}&subfamily_id=${subfamily.id}`
    );

    return response.data.data.map((data) => new StockVehicle(data));
  } catch (e) {
    console.error(e);
  }

  return [];
}

export const useStockItem = (id) => {
  const [stock, setStock] = useState(undefined);

  useEffect(() => {
    axios
      .get(`${API_URL}/stock/stock?id=${id}`)
      .then((response) => {
        if (response.data.data) {
          setStock(new StockVehicle(response.data.data));
          return;
        }

        setStock(null);
      })
      .catch((e) => {
        console.error(e);
        setStock(null);
      });
  }, [id]);

  return stock;
};

export const useServiceOffers = () => {
  const { serviceOffers } = useContext(Context);

  return serviceOffers;
};

export default ContextProvider;
